import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pb';

/* Set up the browser extension — download, install, connect, all on one page.
 *
 * ── Why this page rather than a Slack message ──
 * An unpacked extension is installed from a FOLDER, and it never updates itself: Chrome only
 * auto-updates what came from the Web Store. So instructions sent in a message go stale the first
 * time the extension changes, and the person following them has no way to know. One URL that
 * always offers the current build, with the steps beside it, is the version that stays true.
 *
 * ── Why the token travels this way ──
 * The extension needs to call /api/rof/* as the person using it, and the API's gate is a live
 * PocketBase session belonging to an active roster member. Rather than invent a second way to
 * authenticate — an API key, a shared secret, a device code, all of which would be new attack
 * surface — the extension carries the same token the app itself holds.
 *
 * The alternative considered and rejected: letting the extension read `localStorage` out of an
 * app tab with `chrome.scripting`. It needs no page like this one, but it grants the extension
 * the right to run arbitrary JavaScript on this origin, and it takes the token without asking.
 *
 * ⚠️ This renders INSIDE the signed-in zone, so an unauthenticated visitor meets the ordinary
 * login first and there is nothing to hand over until they are through the roster gate. That is
 * the whole authorisation story — this component adds no check of its own, because it must not
 * be the thing deciding who may connect.
 */

/* Pinned by the `key` in the extension's manifest, so every install shares one id. A copy loaded
   without that key gets a different id and simply will not receive the message — which is the
   correct failure: only the extension we published can be connected. */
const EXTENSION_ID = 'mpnhodkoamkiellndelicakaofmhghcb';

type State =
  | { at: 'idle' }
  | { at: 'sending' }
  | { at: 'done'; name: string }
  | { at: 'failed'; why: string };

/* `chrome` exists only when a Chromium browser has an extension able to receive this. */
interface ChromeRuntime {
  runtime?: {
    sendMessage: (
      id: string,
      message: unknown,
      cb: (response?: { ok?: boolean; name?: string; error?: string }) => void,
    ) => void;
    lastError?: { message?: string };
  };
}

export default function ConnectExtension() {
  const [state, setState] = useState<State>({ at: 'idle' });
  const [version, setVersion] = useState('');

  useEffect(() => {
    document.title = 'Set up the extension — Real or Fake';
    // Published beside the download by the build, so this page cannot advertise a stale version.
    void fetch(`${import.meta.env.BASE_URL}extension.json`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.version && setVersion(d.version))
      .catch(() => {});
  }, []);

  const connect = useCallback(() => {
    const chromeApi = (window as unknown as { chrome?: ChromeRuntime }).chrome;
    if (!chromeApi?.runtime?.sendMessage) {
      setState({ at: 'failed', why: 'No extension found yet. Finish steps 1–4 above, then come back and press Connect.' });
      return;
    }
    setState({ at: 'sending' });
    chromeApi.runtime.sendMessage(
      EXTENSION_ID,
      { type: 'rof-connect', token: pb.authStore.token },
      (response) => {
        /* An uninstalled or differently-built extension surfaces here rather than as an exception,
           and with the vaguest message Chrome has — so it is translated into the thing to do. */
        const missing = chromeApi.runtime?.lastError;
        if (missing || !response?.ok) {
          setState({
            at: 'failed',
            why: missing
              ? 'The extension did not answer. Check it is installed and switched on in chrome://extensions, then try again.'
              : response?.error || 'The extension refused the connection.',
          });
          return;
        }
        setState({ at: 'done', name: response.name || '' });
      },
    );
  }, []);

  return (
    <div className="panel setup" style={{ maxWidth: 680 }}>
      <h2>Add headlines from the article you are reading</h2>
      <p>
        When an article is open in a tab, one keyboard shortcut sends it to Real Headlines or a
        series — no need to come back here and paste the link. It reads the headline, the outlet
        and the date off the page, and tells you if we already have that article.
      </p>

      <p className="hint-sm">
        Chrome only. Takes about two minutes, once{version ? ` · current version ${version}` : ''}.
      </p>

      <ol className="setup-steps">
        <li>
          <a className="btn primary" href={`${import.meta.env.BASE_URL}extension.zip`} download>
            Download the extension
          </a>
          <span className="hint-sm">
            Unzip it and keep the folder somewhere permanent — Documents is fine.
            {' '}<strong>Do not delete or move it afterwards</strong>: Chrome reads the extension
            from wherever you leave it, so moving the folder switches it off.
          </span>
        </li>
        <li>
          Open a new tab and go to <code>chrome://extensions</code>
          <span className="hint-sm">Chrome will not let you click a link to that page — type or paste it.</span>
        </li>
        <li>
          Turn on <strong>Developer mode</strong>, top right.
          <span className="hint-sm">
            It sounds more serious than it is — it is simply how Chrome installs a tool that did
            not come from its store.
          </span>
        </li>
        <li>
          Click <strong>Load unpacked</strong> and choose the folder you unzipped.
        </li>
        <li>
          Come back here and press Connect.
          <span className="hint-sm">
            This signs the extension in as you. You will only ever see what you already have
            access to, and if your access changes the extension follows immediately.
          </span>
        </li>
      </ol>

      {state.at === 'done' ? (
        <div className="setup-done">
          <p className="c-ok"><strong>Connected{state.name ? ` as ${state.name}` : ''}. That is everything.</strong></p>
          <p>
            On any article press <strong>⌘⇧E</strong> to send it to Real Headlines, or
            {' '}<strong>⌘⇧K</strong> to fix the headline and choose a series. On Windows that is
            Ctrl+Shift+E and Ctrl+Shift+K.
          </p>
          <p>
            ⌘⇧E tells you what it did in the corner of the article — the headline it saved, or
            the list it was already in, with an Undo if it was not what you meant. You can also
            right-click any link and send it without opening the page at all.
          </p>
          {/* Said here because those two are only the DEFAULTS — Chrome declines a combination
              another extension already holds and leaves the command unassigned, silently. The
              extension panel shows what is really bound, which is also the answer to "what was
              the shortcut again?". The link itself has to live in the panel: a web page is not
              allowed to link to a chrome:// URL, only an extension is. */}
          <p className="hint-sm">
            Those are the starting shortcuts. Click the Real or Fake icon in the toolbar to see
            the ones you actually have — and to change them to whatever you like.
          </p>
          <p className="hint-sm">
            Nothing you capture reaches Adorian until someone moves it out of the queue, exactly
            as before.
          </p>
          <Link className="btn" to="/">Back to the app</Link>
        </div>
      ) : (
        <>
          <button className="btn primary" onClick={connect} disabled={state.at === 'sending'} data-testid="connect-extension">
            {state.at === 'sending' ? 'Connecting…' : 'Connect'}
          </button>
          {state.at === 'failed' && <p className="c-warn" style={{ marginTop: 12 }}>{state.why}</p>}
        </>
      )}

      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '22px 0 16px' }} />
      {/* ⚠️ "Unzip it over the folder you already have" is the step this paragraph was missing,
          and leaving it out is how somebody sits on a version from three weeks ago believing the
          site is serving it. Chrome loads an unpacked extension from a PATH: the refresh arrow
          re-reads that same folder, so a fresh copy sitting in Downloads changes nothing and the
          card keeps reporting the old version — which reads exactly like a stale download. */}
      <p className="hint-sm">
        <strong>Updating later.</strong> The extension tells you when a newer version is out and
        sends you back here. Download it again and <strong>unzip it over the folder you already
        have</strong> — Chrome loads the extension from that folder, so a new copy in Downloads
        changes nothing. Then on <code>chrome://extensions</code> press the refresh arrow on the
        Real or Fake card, and check the version on the card has gone up. The card also names the
        folder it is loaded from, if you have forgotten which one it is. Your connection carries
        over — you do not have to press Connect again.
      </p>
    </div>
  );
}
